import React, { useCallback, useMemo } from 'react';
import { Box, Grid, TextField, Checkbox, FormControlLabel, InputAdornment, Tabs, Tab } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { nameValidation } from 'utils/Validations';
import { CustomTabPanel, a11yProps, generateRow } from './common';
import { RuleOptions, Holiday, PayRuleFormData } from './common/types';
import { GeneralInfoTab } from './tabs/GeneralInfoTab';
import { HolidaysTab } from './tabs/HolidaysTab';
import { AssignmentRulesTab } from './tabs/AssignmentRulesTab';

export interface PayRulesFormProps {
    formData: PayRuleFormData;
    setFormData: React.Dispatch<React.SetStateAction<PayRuleFormData>>;
    selectedTab: number;
    setSelectedTab: (value: number) => void;
    ruleOptions: RuleOptions;
    holidays: Holiday[];
    loadingWorkRules: boolean;
    existName?: (name: string) => boolean;
    mode: string;
}

export const PayRulesForm: React.FC<PayRulesFormProps> = ({ formData, setFormData, selectedTab, setSelectedTab, ruleOptions, holidays, loadingWorkRules, existName, mode }) => {
    const { t } = useTranslation();

    const handleNameChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            if (nameValidation(value)) {
                setFormData((prev) => ({ ...prev, name: value }));
            }
        },
        [setFormData],
    );

    const nameError = useMemo(() => {
        if (formData.name && existName && mode === 'add') {
            return existName(formData.name) ? t('settings.pay_rules.dialog.alerts.add.exist') : undefined;
        }
        return undefined;
    }, [formData.name, existName, mode, t]);

    return (
        <Box sx={{ width: '100%' }}>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                    <TextField
                        autoFocus
                        required
                        fullWidth
                        name={'name'}
                        label={t('settings.pay_rules.dialog.fields.name')}
                        value={formData.name}
                        onChange={handleNameChange}
                        error={!!nameError}
                        helperText={nameError}
                        slotProps={{
                            htmlInput: { maxLength: 50 },
                            input: {
                                endAdornment: <InputAdornment position="end">{`${formData.name.length}/50`}</InputAdornment>,
                            },
                        }}
                        disabled={mode === 'info'}
                    />
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        name={'description'}
                        label={t('settings.pay_rules.dialog.fields.description')}
                        value={formData.description}
                        onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                        slotProps={{
                            htmlInput: { maxLength: 255 },
                            input: {
                                endAdornment: <InputAdornment position="end">{`${formData.description.length}/255`}</InputAdornment>,
                            },
                        }}
                        disabled={mode === 'info'}
                    />
                </Grid>

                <Grid size={{ xs: 6 }}>
                    <FormControlLabel
                        disabled={mode === 'info'}
                        control={<Checkbox checked={formData.calculateProjectedTotals} onChange={(e) => setFormData((prev) => ({ ...prev, calculateProjectedTotals: e.target.checked }))} />}
                        label={t('settings.pay_rules.dialog.fields.calculate_projected_totals')}
                    />
                </Grid>

                <Grid size={{ xs: 6 }}>
                    <FormControlLabel
                        disabled={mode === 'info'}
                        control={<Checkbox checked={formData.tryPlannedTime} onChange={(e) => setFormData((prev) => ({ ...prev, tryPlannedTime: e.target.checked }))} />}
                        label={t('settings.pay_rules.dialog.fields.try_planned_time')}
                    />
                </Grid>
            </Grid>

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
                    <Tab label={t('settings.pay_rules.dialog.fields.tabs.pay_rules')} {...a11yProps(0)} />
                    <Tab label={t('settings.pay_rules.dialog.fields.tabs.holidays')} {...a11yProps(1)} />
                    <Tab label={t('settings.pay_rules.dialog.fields.tabs.assignment_rules')} {...a11yProps(2)} />
                </Tabs>
            </Box>

            <CustomTabPanel value={selectedTab} index={0}>
                <GeneralInfoTab
                    options={ruleOptions}
                    selections={{
                        fixedRule: formData.fixedRuleSelected,
                        interpretationRule: formData.interpretationRuleSelected,
                        workRule: formData.workRuleSelected,
                        creditHolidayRule: formData.creditHolidayRuleSelected,
                    }}
                    handlers={{
                        setFixedRule: (value) => setFormData((prev) => ({ ...prev, fixedRuleSelected: value })),
                        setInterpretationRule: (value) => setFormData((prev) => ({ ...prev, interpretationRuleSelected: value })),
                        setWorkRule: (value) => setFormData((prev) => ({ ...prev, workRuleSelected: value })),
                        setCreditHolidayRule: (value) => setFormData((prev) => ({ ...prev, creditHolidayRuleSelected: value })),
                    }}
                    mode={mode}
                />
            </CustomTabPanel>

            <CustomTabPanel value={selectedTab} index={1}>
                <HolidaysTab
                    state={{
                        holidays,
                        selected: formData.holidaysSelected,
                    }}
                    handlers={{
                        toggleSelection: (holidayId: number) =>
                            setFormData((prev) => ({
                                ...prev,
                                holidaysSelected: prev.holidaysSelected.includes(holidayId) ? prev.holidaysSelected.filter((id) => id !== holidayId) : [...prev.holidaysSelected, holidayId],
                            })),
                    }}
                    creditHolidayRule={formData.creditHolidayRuleSelected}
                    loading={false}
                    mode={mode}
                />
            </CustomTabPanel>

            <CustomTabPanel value={selectedTab} index={2}>
                <AssignmentRulesTab
                    state={{
                        rows: formData.assignment,
                        loading: loadingWorkRules,
                    }}
                    handlers={{
                        updateRow: (rowId: number, field: string, value: unknown) =>
                            setFormData((prev) => ({
                                ...prev,
                                assignment: prev.assignment.map((row) => (row.id === rowId ? { ...row, [field]: value } : row)),
                            })),
                        moveRowUp: (rowId: number) =>
                            setFormData((prev) => {
                                const index = prev.assignment.findIndex((row) => row.id === rowId);
                                if (index <= 0) return prev;

                                const newRows = [...prev.assignment];
                                const temp = newRows[index].order;
                                newRows[index].order = newRows[index - 1].order;
                                newRows[index - 1].order = temp;

                                [newRows[index], newRows[index - 1]] = [newRows[index - 1], newRows[index]];

                                return { ...prev, assignment: newRows };
                            }),
                        moveRowDown: (rowId: number) =>
                            setFormData((prev) => {
                                const index = prev.assignment.findIndex((row) => row.id === rowId);
                                if (index < 0 || index >= prev.assignment.length - 1) return prev;

                                const newRows = [...prev.assignment];
                                const temp = newRows[index].order;
                                newRows[index].order = newRows[index + 1].order;
                                newRows[index + 1].order = temp;

                                [newRows[index], newRows[index + 1]] = [newRows[index + 1], newRows[index]];

                                return { ...prev, assignment: newRows };
                            }),
                        deleteRow: (rowId: number) =>
                            setFormData((prev) => {
                                const newRows = prev.assignment.filter((row) => row.id !== rowId);
                                return {
                                    ...prev,
                                    assignment: newRows.map((row, index) => ({
                                        ...row,
                                        order: index,
                                    })),
                                };
                            }),
                        addRow: () =>
                            setFormData((prev) => ({
                                ...prev,
                                assignment: [...prev.assignment, generateRow(prev.assignment.length)],
                            })),
                    }}
                    workRules={ruleOptions.workRules}
                    mode={mode}
                />
            </CustomTabPanel>
        </Box>
    );
};
